<template>
  <v-container class="chc-form py-7">
    <div class="chc-form__header">
      <div>
        <v-breadcrumbs class="app-breadcrumb pa-0 mb-2" divider="›" :items="breadcrumbs" />
        <h1>Cadastrar CHC</h1>
        <p>Informe os dados da certidão e relacione os credores correspondentes.</p>
      </div>
      <v-btn outlined color="primary" to="/certidoes-habilitacao"><v-icon left>mdi-arrow-left</v-icon>Voltar</v-btn>
    </div>

    <v-card outlined class="chc-form__steps mb-5">
      <div v-for="(item, index) in steps" :key="item" class="chc-form__step">
        <span :class="{ active: index === step, done: index < step }">
          <v-icon v-if="index < step" x-small color="white">mdi-check</v-icon><template v-else>{{ index + 1 }}</template>
        </span>
        <small :class="{ 'primary--text font-weight-bold': index === step }">{{ item }}</small>
        <i v-if="index < steps.length - 1" :class="{ done: index < step }" />
      </div>
    </v-card>

    <v-form ref="form" v-model="valid">
      <v-card outlined class="chc-form__card">
        <v-window v-model="step">
          <v-window-item :value="0">
            <section>
              <h2>Credores e processo</h2>
              <p>Localize e selecione os credores relacionados à certidão.</p>
              <v-autocomplete v-model="selectedCreditor" :items="creditorOptions" :search-input.sync="creditorSearch"
                item-text="display" return-object outlined dense clearable hide-no-data
                label="Buscar credor por nome ou CPF/CNPJ" :loading="searchingCreditors"
                @change="addSelectedCreditor" />
              <div v-for="(creditor, index) in data.creditors" :key="creditor.registration" class="chc-form__selected">
                <div><strong>{{ creditor.name }}</strong><small>{{ formatRegistration(creditor.registration) }}</small></div>
                <v-btn icon color="error" @click="removeCreditor(index)"><v-icon>mdi-delete-outline</v-icon></v-btn>
              </div>
              <v-divider class="my-6" />
              <v-row>
                <v-col cols="12" md="6"><v-text-field v-model="data.origin_process" outlined dense label="Processo de origem (CNJ)" :rules="[required]" /></v-col>
                <v-col cols="12" md="2"><v-select v-model="data.process_uf" :items="states" outlined dense label="UF" :rules="[required]" /></v-col>
                <v-col cols="12" md="4"><v-select v-model="data.justice_branch" :items="justiceBranches" outlined dense label="Ramo da Justiça" :rules="[required]" /></v-col>
                <v-col cols="12" md="6"><v-text-field v-model="data.judging_body" outlined dense label="Órgão julgador" :rules="[required]" /></v-col>
                <v-col cols="12" md="3"><v-text-field v-model="data.final_judgment_at" outlined dense type="date" label="Trânsito em julgado" :rules="[required]" /></v-col>
                <v-col cols="12" md="3"><v-text-field v-model="data.calculation_updated_at" outlined dense type="date" label="Atualização dos cálculos" :rules="[required]" /></v-col>
              </v-row>
            </section>
          </v-window-item>

          <v-window-item :value="1">
            <section>
              <div class="d-flex align-center"><div><h2>Créditos</h2><p>Adicione todos os créditos contemplados pela certidão.</p></div><v-spacer /><v-btn outlined color="primary" @click="addCredit"><v-icon left>mdi-plus</v-icon>Adicionar crédito</v-btn></div>
              <v-card v-for="(credit, index) in data.credits" :key="index" outlined class="pa-4 mb-4">
                <div class="d-flex"><strong>Crédito {{ index + 1 }}</strong><v-spacer /><v-btn v-if="data.credits.length > 1" icon small color="error" @click="data.credits.splice(index, 1)"><v-icon>mdi-delete-outline</v-icon></v-btn></div>
                <v-row class="mt-1">
                  <v-col cols="12" md="4"><v-select v-model="credit.registration" :items="creditorItems" outlined dense label="Credor vinculado" :rules="[required]" /></v-col>
                  <v-col cols="12" md="2"><v-select v-model="credit.currency" :items="currencies" item-text="text" item-value="value" outlined dense label="Moeda" :rules="[required]" /></v-col>
                  <v-col cols="12" md="3"><v-text-field v-model="credit.amount" outlined dense type="number" min="0.01" step="0.01" label="Valor do crédito" :rules="[required]" /></v-col>
                  <v-col cols="12" md="3"><v-select v-model="credit.class_type" :items="classes" item-text="text" item-value="value" outlined dense label="Classe" :rules="[required]" /></v-col>
                  <v-col cols="12"><v-select v-model="credit.credit_origin" :items="creditOrigins" outlined dense label="Origem do crédito" :rules="[required]" /></v-col>
                </v-row>
              </v-card>
            </section>
          </v-window-item>

          <v-window-item :value="2">
            <section>
              <h2>Outras verbas</h2>
              <p>Os campos reproduzem integralmente os dados solicitados ao credor.</p>
              <v-switch v-model="data.has_attorney_fees" color="primary" label="Há honorários advocatícios?" />
              <div v-if="data.has_attorney_fees">
                <div class="d-flex mb-3"><h3>Honorários advocatícios</h3><v-spacer /><v-btn small outlined color="primary" @click="addAttorneyFee"><v-icon left>mdi-plus</v-icon>Adicionar</v-btn></div>
                <v-card v-for="(fee, index) in data.attorney_fees" :key="`a-${index}`" outlined class="pa-4 mb-4">
                  <div class="d-flex"><strong>Advogado {{ index + 1 }}</strong><v-spacer /><v-btn v-if="data.attorney_fees.length > 1" icon small color="error" @click="data.attorney_fees.splice(index, 1)"><v-icon>mdi-delete-outline</v-icon></v-btn></div>
                  <v-row class="mt-1">
                    <v-col cols="12" md="4"><v-text-field v-model="fee.name" outlined dense label="Nome do advogado" :rules="[required]" /></v-col>
                    <v-col cols="12" md="3"><v-text-field v-model="fee.registration" outlined dense label="CPF" :rules="[required]" /></v-col>
                    <v-col cols="12" md="2"><v-text-field v-model="fee.oab" outlined dense label="OAB" :rules="[required]" /></v-col>
                    <v-col cols="12" md="1"><v-select v-model="fee.uf" :items="states" outlined dense label="UF" :rules="[required]" /></v-col>
                    <v-col cols="12" md="2"><v-text-field v-model="fee.amount" outlined dense type="number" label="Valor" :rules="[required]" /></v-col>
                    <v-col cols="12" md="4"><v-text-field v-model="fee.fixed_at" outlined dense type="date" label="Data de fixação" :rules="[required]" /></v-col>
                    <v-col cols="12" md="8"><v-file-input v-model="fee.document_file" outlined dense prepend-icon="" prepend-inner-icon="mdi-paperclip" label="Documento dos honorários (opcional)" /></v-col>
                  </v-row>
                </v-card>
              </div>
              <v-divider class="my-5" />
              <v-switch v-model="data.has_expert_fees" color="primary" label="Há honorários periciais?" />
              <div v-if="data.has_expert_fees">
                <div class="d-flex mb-3"><h3>Honorários periciais</h3><v-spacer /><v-btn small outlined color="primary" @click="addExpertFee"><v-icon left>mdi-plus</v-icon>Adicionar</v-btn></div>
                <v-card v-for="(fee, index) in data.expert_fees" :key="`e-${index}`" outlined class="pa-4 mb-4">
                  <div class="d-flex"><strong>Perito {{ index + 1 }}</strong><v-spacer /><v-btn v-if="data.expert_fees.length > 1" icon small color="error" @click="data.expert_fees.splice(index, 1)"><v-icon>mdi-delete-outline</v-icon></v-btn></div>
                  <v-row class="mt-1">
                    <v-col cols="12" md="4"><v-text-field v-model="fee.name" outlined dense label="Nome do perito" :rules="[required]" /></v-col>
                    <v-col cols="12" md="3"><v-text-field v-model="fee.registration" outlined dense label="CPF/CNPJ" :rules="[required]" /></v-col>
                    <v-col cols="12" md="2"><v-text-field v-model="fee.amount" outlined dense type="number" label="Valor" :rules="[required]" /></v-col>
                    <v-col cols="12" md="3"><v-text-field v-model="fee.fixed_at" outlined dense type="date" label="Data de fixação" :rules="[required]" /></v-col>
                    <v-col cols="12"><v-file-input v-model="fee.document_file" outlined dense prepend-icon="" prepend-inner-icon="mdi-paperclip" label="Documento dos honorários (opcional)" /></v-col>
                  </v-row>
                </v-card>
              </div>
            </section>
          </v-window-item>

          <v-window-item :value="3">
            <section>
              <h2>Documentos</h2>
              <p>Arraste os arquivos para os campos ou clique para selecioná-los.</p>
              <v-row>
                <v-col cols="12" md="6"><file-drop v-model="files.chc" label="Certidão de Habilitação de Crédito" required /></v-col>
                <v-col cols="12" md="6"><file-drop v-model="files.homologation" label="Decisão homologatória (opcional)" /></v-col>
                <v-col cols="12" md="6"><file-drop v-model="files.spreadsheet" label="Planilha de cálculos (opcional)" accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.csv" /></v-col>
                <v-col cols="12" md="6">
                  <v-switch v-model="data.is_confidential" color="primary" label="O processo de origem é sigiloso?" />
                  <file-drop v-if="data.is_confidential" v-model="files.confidential" label="Processo integral sigiloso" required />
                </v-col>
                <v-col cols="12"><v-textarea v-model="data.observations" outlined counter="1000" label="Observações (opcional)" /></v-col>
              </v-row>
            </section>
          </v-window-item>

          <v-window-item :value="4">
            <section>
              <h2>Conferência e envio</h2>
              <v-alert color="#EEF1FB" border="left" colored-border>
                O cadastro não aprova nem altera solicitações existentes. Ele cria um novo registro de CHC para acompanhamento.
              </v-alert>
              <v-row>
                <v-col cols="12" md="6"><summary-card title="Processo" :lines="[data.origin_process, `${data.justice_branch} · ${data.process_uf}`, data.judging_body]" /></v-col>
                <v-col cols="12" md="6"><summary-card title="Credores relacionados" :lines="data.creditors.map(item => `${item.name} · ${formatRegistration(item.registration)}`)" /></v-col>
                <v-col cols="12" md="6"><summary-card title="Créditos" :lines="data.credits.map(item => `${currencyLabel(item.currency)} · ${formatMoney(item.amount)} · ${classLabel(item.class_type)}`)" /></v-col>
                <v-col cols="12" md="6"><summary-card title="Documentos" :lines="documentSummary" /></v-col>
              </v-row>
              <v-checkbox v-model="confirmed" color="primary" label="Revisei os dados e confirmo o cadastro com os credores relacionados." />
            </section>
          </v-window-item>
        </v-window>

        <v-divider />
        <v-card-actions class="pa-6">
          <v-btn v-if="step > 0" text color="primary" @click="step--"><v-icon left>mdi-arrow-left</v-icon>Voltar</v-btn>
          <v-spacer />
          <v-btn v-if="step < 4" color="primary" elevation="0" @click="next">Continuar<v-icon right>mdi-arrow-right</v-icon></v-btn>
          <v-btn v-else color="success" elevation="0" :disabled="!confirmed" :loading="saving" @click="submit">
            <v-icon left>mdi-check</v-icon>Cadastrar CHC
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <v-dialog v-model="success" max-width="520" persistent>
      <v-card class="text-center pa-8">
        <v-avatar color="#E6F4EA" size="64"><v-icon color="success" size="34">mdi-check</v-icon></v-avatar>
        <h2 class="mt-5">CHC cadastrada</h2>
        <p class="mt-2">O registro foi criado com sucesso e já consta no histórico da devedora.</p>
        <v-btn color="primary" elevation="0" to="/certidoes-habilitacao">Voltar ao histórico</v-btn>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";

const FileDrop = {
  props: { value: null, label: String, required: Boolean, accept: { type: String, default: ".pdf,.jpg,.jpeg,.png" } },
  template: `<div class="chc-file-drop"><v-icon size="34" color="primary">{{ value ? "mdi-file-check-outline" : "mdi-cloud-upload-outline" }}</v-icon><strong>{{ value ? value.name : label }}</strong><small>{{ value ? "Arquivo selecionado" : "Arraste aqui ou clique para selecionar" }}</small><v-file-input :value="value" @change="$emit('input', $event)" :accept="accept" prepend-icon="" hide-details class="chc-file-drop__input" /></div>`,
};
const SummaryCard = { props: { title: String, lines: Array }, template: `<v-card outlined class="pa-4 fill-height"><strong>{{ title }}</strong><div v-for="(line,index) in lines" :key="index" class="mt-2">{{ line || "—" }}</div></v-card>` };

export default {
  name: "CreditCertificateForm",
  components: { FileDrop, SummaryCard },
  data: () => ({
    breadcrumbs: [{ text: "Certidões de Habilitação de Crédito", to: "/certidoes-habilitacao", exact: true }, { text: "Cadastrar CHC", disabled: true }],
    steps: ["Credores e processo", "Créditos", "Outras verbas", "Documentos", "Conferência"],
    step: 0, valid: false, confirmed: false, saving: false, success: false,
    creditorSearch: "", searchingCreditors: false, selectedCreditor: null, creditorOptions: [],
    states: ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"],
    justiceBranches: ["Justiça Estadual", "Justiça Federal", "Justiça do Trabalho", "Justiça Eleitoral", "Justiça Militar", "Tribunal Superior"],
    currencies: [{ text: "BRL (R$)", value: 1 }, { text: "USD ($)", value: 2 }, { text: "EUR (€)", value: 3 }],
    classes: [
      { text: "Classe I - Trabalhista", value: 1 }, { text: "Classe II - Garantia Real", value: 2 },
      { text: "Classe III - Quirografários", value: 3 }, { text: "Classe IV - ME/EPP", value: 4 },
      { text: "Extraconcursais", value: 5 }, { text: "Ilíquidos", value: 6 }, { text: "Fiscal", value: 7 },
      { text: "Não sujeito", value: 8 }, { text: "Não subordinados", value: 9 },
    ],
    creditOrigins: ["Sentença transitada em julgado", "Acordo judicial homologado", "Decisão homologatória de cálculos", "Honorários advocatícios", "Honorários periciais", "Outro"],
    files: { chc: null, homologation: null, spreadsheet: null, confidential: null },
    data: {
      origin_process: "", process_uf: "", justice_branch: "", judging_body: "",
      final_judgment_at: "", calculation_updated_at: "", observations: "", is_confidential: false,
      creditors: [], credits: [{ registration: "", currency: 1, amount: "", credit_origin: "", class_type: "" }],
      has_attorney_fees: false, attorney_fees: [],
      has_expert_fees: false, expert_fees: [],
    },
    required: (value) => (value !== null && value !== undefined && value !== "") || "Campo obrigatório",
    searchTimer: null,
  }),
  computed: {
    creditorItems() { return this.data.creditors.map((item) => ({ text: `${item.name} · ${this.formatRegistration(item.registration)}`, value: item.registration })); },
    documentSummary() {
      const result = [this.files.chc ? `CHC: ${this.files.chc.name}` : "CHC não selecionada"];
      if (this.files.homologation) result.push(`Decisão: ${this.files.homologation.name}`);
      if (this.files.spreadsheet) result.push(`Cálculos: ${this.files.spreadsheet.name}`);
      if (this.files.confidential) result.push(`Processo integral: ${this.files.confidential.name}`);
      return result;
    },
  },
  watch: {
    creditorSearch(value) {
      clearTimeout(this.searchTimer);
      if (!value || value.length < 2) return;
      this.searchTimer = setTimeout(() => this.searchCreditors(value), 300);
    },
  },
  methods: {
    async searchCreditors(search) {
      this.searchingCreditors = true;
      try {
        const response = await axios.get("/api/client/credit-certificate-requests/creditors", { params: { search } });
        this.creditorOptions = (response.data.content || []).map((item) => ({ ...item, display: `${item.name} · ${this.formatRegistration(item.registration)}` }));
      } finally { this.searchingCreditors = false; }
    },
    addSelectedCreditor(item) {
      if (!item || this.data.creditors.some((creditor) => creditor.registration === item.registration)) return;
      this.data.creditors.push({ name: item.name, registration: String(item.registration).replace(/\D/g, "") });
      if (this.data.credits.length === 1 && !this.data.credits[0].registration) this.data.credits[0].registration = String(item.registration).replace(/\D/g, "");
      this.$nextTick(() => { this.selectedCreditor = null; this.creditorSearch = ""; });
    },
    removeCreditor(index) {
      const registration = this.data.creditors[index].registration;
      this.data.creditors.splice(index, 1);
      this.data.credits.forEach((credit) => { if (credit.registration === registration) credit.registration = ""; });
    },
    addCredit() { this.data.credits.push({ registration: "", currency: 1, amount: "", credit_origin: "", class_type: "" }); },
    addAttorneyFee() { this.data.attorney_fees.push({ name: "", registration: "", oab: "", uf: "", amount: "", fixed_at: "", document_file: null }); },
    addExpertFee() { this.data.expert_fees.push({ name: "", registration: "", amount: "", fixed_at: "", document_file: null }); },
    next() {
      const missing = (value) => value === null || value === undefined || value === "";
      if (this.step === 0 && (!this.data.creditors.length ||
        [this.data.origin_process, this.data.process_uf, this.data.justice_branch, this.data.judging_body, this.data.final_judgment_at, this.data.calculation_updated_at].some(missing)))
        return this.notify("Preencha os credores e todos os dados do processo.");
      if (this.step === 1 && this.data.credits.some((credit) =>
        [credit.registration, credit.currency, credit.amount, credit.credit_origin, credit.class_type].some(missing)))
        return this.notify("Preencha todos os dados dos créditos.");
      if (this.step === 2) {
        if (this.data.has_attorney_fees && !this.data.attorney_fees.length) return this.notify("Adicione ao menos um honorário advocatício.");
        if (this.data.has_expert_fees && !this.data.expert_fees.length) return this.notify("Adicione ao menos um honorário pericial.");
        const attorneyMissing = this.data.attorney_fees.some((fee) => [fee.name, fee.registration, fee.oab, fee.uf, fee.amount, fee.fixed_at].some(missing));
        const expertMissing = this.data.expert_fees.some((fee) => [fee.name, fee.registration, fee.amount, fee.fixed_at].some(missing));
        if (attorneyMissing || expertMissing) return this.notify("Preencha todos os dados dos honorários.");
      }
      if (this.step === 3 && !this.files.chc) return this.notify("Selecione o documento CHC.");
      if (this.step === 3 && this.data.is_confidential && !this.files.confidential) return this.notify("Selecione o processo integral sigiloso.");
      this.step += 1;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    notify(message) { this.$dialog.notify.error(message, { position: "top-right" }); },
    buildPayload() {
      const form = new FormData();
      const data = {
        ...this.data,
        debtor_client_ids: [],
        attorney_fees: this.data.has_attorney_fees ? this.data.attorney_fees.map(({ document_file, ...fee }) => fee) : [],
        expert_fees: this.data.has_expert_fees ? this.data.expert_fees.map(({ document_file, ...fee }) => fee) : [],
        confidential_warning_confirmed: this.data.is_confidential,
      };
      ["creditors", "credits", "attorney_fees", "expert_fees", "debtor_client_ids"].forEach((key) => form.append(key, JSON.stringify(data[key])));
      Object.keys(data).filter((key) => !["creditors", "credits", "attorney_fees", "expert_fees", "debtor_client_ids"].includes(key)).forEach((key) => form.append(key, data[key]));
      form.append("chc_file", this.files.chc);
      if (this.files.homologation) form.append("homologation_decision_file", this.files.homologation);
      if (this.files.spreadsheet) form.append("calculation_spreadsheet_file", this.files.spreadsheet);
      if (this.files.confidential) form.append("confidential_full_process_file", this.files.confidential);
      this.data.attorney_fees.forEach((fee, index) => { if (fee.document_file) form.append(`attorney_fee_document_${index}`, fee.document_file); });
      this.data.expert_fees.forEach((fee, index) => { if (fee.document_file) form.append(`expert_fee_document_${index}`, fee.document_file); });
      return form;
    },
    async submit() {
      this.saving = true;
      try {
        await axios.post("/api/client/credit-certificate-requests", this.buildPayload(), {
          headers: { "Idempotency-Key": this.idempotencyKey(), "Content-Type": "multipart/form-data" },
        });
        this.success = true;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally { this.saving = false; }
    },
    idempotencyKey() {
      if (!this._idempotencyKey) this._idempotencyKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return this._idempotencyKey;
    },
    formatRegistration(value) {
      const d = String(value || "").replace(/\D/g, "");
      if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value || "";
    },
    formatMoney(value) { return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }); },
    currencyLabel(value) { return (this.currencies.find((item) => item.value === value) || {}).text || ""; },
    classLabel(value) { return (this.classes.find((item) => item.value === value) || {}).text || ""; },
  },
};
</script>

<style scoped>
.chc-form { max-width: 1280px; }
.chc-form__header { display:flex; align-items:center; justify-content:space-between; gap:20px; margin-bottom:24px; }
.chc-form__header h1 { color:#101828; font-size:28px; }
.chc-form__header p, section > p { color:#667085; margin-top:6px; }
.chc-form__card, .chc-form__steps { border-color:#e4e7ec !important; border-radius:10px; }
.chc-form__steps { display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); padding:20px 24px; }
.chc-form__step { display:flex; align-items:center; min-width:0; gap:9px; color:#667085; }
.chc-form__step > span { display:flex; width:30px; height:30px; flex:0 0 30px; align-items:center; justify-content:center; border:1px solid #d0d5dd; border-radius:50%; }
.chc-form__step > span.active { border-color:#2a3070; background:#eef1fb; color:#2a3070; font-weight:700; }
.chc-form__step > span.done { border-color:#2a3070; background:#2a3070; color:#fff; }
.chc-form__step > i { flex:1; height:1px; background:#e4e7ec; }
.chc-form__step > i.done { background:#2a3070; }
section { min-height:470px; padding:28px; }
section h2 { color:#101828; font-size:21px; }
.chc-form__selected { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border:1px solid #e4e7ec; border-radius:8px; margin-top:8px; }
.chc-form__selected small { display:block; color:#667085; }
@media (max-width: 900px) {
  .chc-form__steps { overflow-x:auto; grid-template-columns:repeat(5,180px); }
  .chc-form__header { align-items:flex-start; flex-direction:column; }
}
</style>

<style>
.chc-file-drop { position:relative; display:flex; min-height:150px; flex-direction:column; align-items:center; justify-content:center; gap:8px; padding:18px; border:1.5px dashed #98a2b3; border-radius:10px; background:#fafbfc; text-align:center; }
.chc-file-drop small { color:#667085; }
.chc-file-drop__input { position:absolute !important; inset:0; opacity:0; }
.chc-file-drop__input .v-input__control, .chc-file-drop__input .v-input__slot { height:100%; }
</style>
